import { motion } from 'framer-motion'
import { Building2, Shield, Users, Headphones, Server, Lock, Briefcase, Globe, Phone, FileCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'

// Same order as features.items in enterprise.json.
const featureIcons = [Shield, Server, Users, Lock, Headphones, FileCheck]

const compliance = ['ISO 27001', 'SOC 2 Type II', 'GDPR', 'HIPAA', 'PCI DSS']

export default function Enterprise() {
  const { t } = useTranslation(['enterprise', 'common'])
  const featureTexts = t('features.items', { returnObjects: true }) as { title: string; description: string }[]
  const features = featureIcons.map((icon, i) => ({ icon, ...featureTexts[i] }))
  const enterpriseStats = t('stats', { returnObjects: true }) as { value: string; label: string }[]

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}  className="pt-32 pb-24">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/enterprise"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.enterprise'), url: '/enterprise' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Building2 className="w-4 h-4" /> {t('hero.badge')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('hero.title')} <span className="text-gradient">{t('hero.titleAccent')}</span><br />{t('hero.titleLine2')}
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto mb-8">
            {t('hero.text')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all">
              <Phone className="w-5 h-5" /> {t('hero.sales')}
            </Link>
            <button className="flex items-center gap-2 px-8 py-4 glass hover:bg-white/10 text-white font-semibold rounded-xl transition-all">
              <Briefcase className="w-5 h-5" /> {t('hero.demo')}
            </button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {enterpriseStats.map((stat) => (
            <div key={stat.label} className="glass rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-gradient mb-2">{stat.value}</div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Features Grid */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20">
          <h2 className="text-3xl font-display font-bold text-white text-center mb-12">{t('features.title')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div key={feature.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }} className="glass rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Compliance */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass rounded-2xl p-8 mb-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">{t('compliance.title')}</h2>
              <p className="text-gray-400">{t('compliance.text')}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {compliance.map((cert) => (
                <span key={cert} className="px-4 py-2 bg-primary/10 text-primary rounded-lg font-medium">{cert}</span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
          <h2 className="text-3xl font-display font-bold text-white mb-6">{t('cta.title')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('cta.text')}</p>
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 text-gray-400"><Phone className="w-5 h-5" /><span>+90 850 123 45 67</span></div>
            <span className="text-gray-600">|</span>
            <div className="flex items-center gap-2 text-gray-400"><Globe className="w-5 h-5" /><span>sales@cmdesk.help</span></div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}
